import React, { useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

type PortfolioProps = {
  onExitPrev?: () => void;
  onExitNext?: () => void;
  /** Cambiar este valor reinicia el scroll interno al primer video. */
  resetKey?: string | null;
};

// Base pública donde viven los MP4 de los comerciales (Cloudflare Pages,
// proyecto pd-web-videos, cuenta gpd.bot@penadefillo.com).
const VIDEO_BASE = "https://pd-web-videos.pages.dev";

const videos = [
  {
    src: `${VIDEO_BASE}/plaza-lama-tecnomaster.mp4`,
    pretitle: "Trabajo",
    title: "Plaza Lama | Tecnomaster",
    description: "Maestros de la tecnología",
  },
  {
    src: `${VIDEO_BASE}/mcdonalds-pollo-mccrispy.mp4`,
    pretitle: "Trabajo",
    title: "McDonalds | Pollo McCrispy",
    description: "Fresco | Crujiente | Sabroso",
  },
  {
    src: `${VIDEO_BASE}/map-la-frontera.mp4`,
    pretitle: "Trabajo",
    title: "Ministerio de Administración Pública | La frontera",
    description:
      "La frontera no es donde termina la República Dominicana. Es donde empieza el deber de protegerla",
  },
  {
    src: `${VIDEO_BASE}/tecnomaster-maestro.mp4`,
    pretitle: "Trabajo",
    title: "Tecnomaster | Maestro",
    description: "Maestros de la tecnología.",
  },
];

const Portfolio: React.FC<PortfolioProps> = ({
  onExitPrev,
  onExitNext,
  resetKey,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<HTMLDivElement[]>([]);

  // Al navegar al portafolio (resetKey nuevo), entrar siempre por el primer
  // video; el contenedor interno conserva su scroll y sin esto se aterriza
  // en el último video visitado.
  useEffect(() => {
    if (resetKey == null) return;
    containerRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [resetKey]);

  // Play/pause only the snapped (mostly visible) video
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector("video");
          if (!video) return;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.6 }
    );

    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Allow exiting portfolio when user scrolls beyond its bounds
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      const el = containerRef.current;
      if (!el) return;

      const atTop = el.scrollTop <= 0;
      const atBottom =
        Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;

      if (e.deltaY < 0 && atTop) {
        onExitPrev?.();
        e.preventDefault();
        return;
      }
      if (e.deltaY > 0 && atBottom) {
        onExitNext?.();
        e.preventDefault();
        return;
      }
    },
    [onExitPrev, onExitNext]
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      ref={containerRef}
      onWheel={handleWheel}
      className="h-screen w-screen overflow-y-auto snap-y snap-mandatory scroll-smooth"
      viewport={{ once: true, amount: 0.5 }}
    >
      {videos.map((video, index) => (
        <section
          key={`${video.src}-${index}`}
          ref={(el) => (sectionRefs.current[index] = el as HTMLDivElement)}
          className="relative w-screen snap-start snap-always"
          style={{ height: "100svh" }}
        >
          {/* Video full-bleed background */}
          <div className="absolute inset-0 -z-10 pointer-events-none bg-black overflow-hidden">
            <video
              src={video.src}
              autoPlay={index === 0}
              loop
              muted
              playsInline
              preload={index === 0 ? "auto" : "metadata"}
              disablePictureInPicture
              className="absolute top-0 left-0 h-full w-full object-cover"
            />
          </div>

          {/* Overlays for readability */}
          <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
          <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-black/40 via-transparent to-transparent" />

          {/* Centered, responsive text overlay */}
          <div className="relative z-10 h-full w-full flex items-center justify-center">
            <div className="absolute z-10 top-1/2 left-0 -translate-y-1/2 w-full max-w-5xl px-6 sm:px-10 md:px-14 text-white select-none text-left">
              {video.pretitle && (
                <h6 className="font-semibold tracking-wide text-xs sm:text-sm md:text-base leading-none mb-3 opacity-90">
                  {video.pretitle}
                </h6>
              )}
              <h2 className="font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4">
                {video.title}
              </h2>
              <p className="font-medium text-base sm:text-lg lg:text-xl opacity-90 max-w-3xl mx-auto md:mx-0">
                {video.description}
              </p>
            </div>
          </div>
        </section>
      ))}
    </motion.div>
  );
};

export default Portfolio;
